import { IPolicyAdminProvider } from "../interfaces/IPolicyAdminProvider";
import {
  Application,
  ApplicationDocument,
  ApplicationDocumentType,
  PolicyAdminProviderConfig,
} from "../types/PolicyAdmin.types";
import {
  SurefyreApplication,
  SurefyreAssignedRole,
  SurefyreAssignedRoleResponse,
  SurefyreAttachment,
  SurefyreDocument,
  SurefyreDocumentResponse,
  SurefyreUser,
} from "../types/Surefyre.types";
import { Result } from "@badrap/result";
import { config } from "../../../config/env";

export class SurefyreProvider implements IPolicyAdminProvider {
  private config: PolicyAdminProviderConfig;

  constructor(config: PolicyAdminProviderConfig) {
    this.config = config;
  }

  async getApplicationById(id: string): Promise<Result<Application, Error>> {
    try {
      const applicationResult = await this.getSurefyreApplicationById(id);
      if (applicationResult.isErr) {
        return Result.err(applicationResult.error);
      }
      const application = applicationResult.value;

      const roleResult = await this.getUnderwriter(id);
      let underwriter = "N/A";

      if (roleResult.isOk && roleResult.value) {
        const userResult = await this.getSurefyreUser(roleResult.value.user_id);
        if (userResult.isOk) {
          underwriter =
            userResult.value.first_name + " " + userResult.value.last_name;
        }
      }

      return Result.ok({
        id: application.id,
        number: application.application_number,
        type: application.type,
        effective_date: application.model.EffectiveDate || "",
        expiration_date: application.model.ExpirationDate || "",
        program: application.model.Program || "",
        named_insured: application.model.AccountName || "",
        total_locations_limit: application.model.TotalLocationsLimit,
        internal_underwriting_notes:
          application.model.InternalUnderwritingNotes || "",
        underwriter: underwriter,
      });
    } catch (error) {
      return Result.err(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  async getDocuments(
    objectId: string
  ): Promise<Result<ApplicationDocument[], Error>> {
    try {
      const docsResult = await this.getSurefyreDocuments(objectId);
      if (docsResult.isErr) {
        return Result.err(docsResult.error);
      }

      const attachResult = await this.getSurefyreAttachments(objectId);
      if (attachResult.isErr) {
        return Result.err(attachResult.error);
      }

      return Result.ok([...docsResult.value, ...attachResult.value]);
    } catch (error) {
      return Result.err(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  private async getSurefyreApplicationById(
    id: string
  ): Promise<Result<SurefyreApplication, Error>> {
    try {
      const url = `${config.surefyre.getApplicationByIdUrl}/${id}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.config.apiKey,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return Result.err(
            new Error(`Application with ID '${id}' not found in Surefyre`)
          );
        }

        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorBody = await response.text();
          if (errorBody) {
            errorMessage += ` - ${errorBody}`;
          }
        } catch (e) {
          return Result.err(new Error("Could not read error response body"));
        }

        return Result.err(new Error(errorMessage));
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        return Result.err(
          new Error(
            `Expected JSON response but got ${contentType}. Response may be an error page.`
          )
        );
      }

      const data: SurefyreApplication = await response.json();
      return Result.ok(data);
    } catch (error) {
      return Result.err(
        error instanceof Error
          ? error
          : new Error("Unknown error occurred in Surefyre provider")
      );
    }
  }

  private getDocumentName(
    document: SurefyreDocument | SurefyreAttachment
  ): string {
    const validExtensions = [
      ".pdf",
      ".png",
      ".jpg",
      ".jpeg",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
      ".csv",
      ".txt",
      ".rtf",
    ];
    const hasValidExtension = validExtensions.some((ext) =>
      document.name.toLowerCase().endsWith(ext)
    );

    if (!hasValidExtension) {
      const urlWithoutQuery = document.download_url.split("?")[0];
      const urlParts = urlWithoutQuery.split(".");
      const urlExtension =
        urlParts.length > 1 ? urlParts[urlParts.length - 1] : "";

      const extension = validExtensions.includes(
        `.${urlExtension.toLowerCase()}`
      )
        ? `.${urlExtension}`
        : ".pdf";
      document.name += extension;
      return document.name;
    }
    return document.name;
  }

  private async getSurefyreDocuments(
    objectId: string
  ): Promise<Result<ApplicationDocument[], Error>> {
    try {
      const url = `${config.surefyre.getDocumentsUrl}/${objectId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.config.apiKey,
        },
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorBody = await response.text();
          if (errorBody) {
            errorMessage += ` - ${errorBody}`;
          }
        } catch (e) {
          return Result.err(new Error("Could not read error response body"));
        }
        return Result.err(new Error(errorMessage));
      }

      const data: SurefyreDocumentResponse = await response.json();
      const documents = data.results;

      const returnDocuments: ApplicationDocument[] = [];

      const hazardHub = documents.filter((doc) => doc.group === "Hazard Hub");
      if (hazardHub.length > 0) {
        const latestHazardHub = hazardHub.sort((a, b) => {
          if (a.created && b.created) {
            return (
              new Date(b.created).getTime() - new Date(a.created).getTime()
            );
          }
          return (b.id || 0) - (a.id || 0);
        })[0];

        if (latestHazardHub && latestHazardHub.download_url) {
          returnDocuments.push({
            name: this.getDocumentName(latestHazardHub),
            url: latestHazardHub.download_url,
            type: ApplicationDocumentType.HAZARD_HUB,
            created: latestHazardHub.created,
          });
        }
      }

      const referDocs = documents.filter((doc) => doc.group === "Refer");
      for (const referDoc of referDocs) {
        if (!referDoc.download_url) {
          continue;
        }
        returnDocuments.push({
          name: this.getDocumentName(referDoc),
          url: referDoc.download_url,
          type: ApplicationDocumentType.OTHER_DOCUMENT,
          created: referDoc.created,
        });
      }

      return Result.ok(returnDocuments);
    } catch (error) {
      return Result.err(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  private async getSurefyreAttachments(
    objectId: string
  ): Promise<Result<ApplicationDocument[], Error>> {
    try {
      const url = `${config.surefyre.getAttachmentsUrl}/${objectId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.config.apiKey,
        },
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorBody = await response.text();
          if (errorBody) {
            errorMessage += ` - ${errorBody}`;
          }
        } catch (e) {
          return Result.err(new Error("Could not read error response body"));
        }
        return Result.err(new Error(errorMessage));
      }

      const data: SurefyreAttachment[] = await response.json();
      const lossRuns = data.filter((att) => att.group === "Loss Runs");
      const returnAttachments: ApplicationDocument[] = [];

      if (lossRuns.length > 0) {
        const latestLossRun = lossRuns.sort((a, b) => {
          if (a.created && b.created) {
            return (
              new Date(b.created).getTime() - new Date(a.created).getTime()
            );
          }
          return (b.id || 0) - (a.id || 0);
        })[0];

        if (latestLossRun && latestLossRun.download_url) {
          returnAttachments.push({
            name: this.getDocumentName(latestLossRun),
            url: latestLossRun.download_url,
            type: ApplicationDocumentType.OTHER_DOCUMENT,
            created: latestLossRun.created,
          });
        }
      }

      const referAttachments = data.filter((att) => att.group === "Refer");
      for (const referAttachment of referAttachments) {
        if (!referAttachment.download_url) {
          continue;
        }
        returnAttachments.push({
          name: this.getDocumentName(referAttachment),
          url: referAttachment.download_url,
          type: ApplicationDocumentType.OTHER_DOCUMENT,
          created: referAttachment.created,
        });
      }
      return Result.ok(returnAttachments);
    } catch (error) {
      return Result.err(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  private async getUnderwriter(
    applicationId: string
  ): Promise<Result<SurefyreAssignedRole | undefined, Error>> {
    try {
      const url = `${config.surefyre.getAssignedRoleUrl}/${applicationId}/`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.config.apiKey,
        },
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorBody = await response.text();
          if (errorBody) {
            errorMessage += ` - ${errorBody}`;
          }
        } catch (e) {
          return Result.err(new Error("Could not read error response body"));
        }
        return Result.err(new Error(errorMessage));
      }

      const data: SurefyreAssignedRoleResponse = await response.json();
      const underwriterRole = data.results.find(
        (role) => role.role === "underwriter"
      );
      return Result.ok(underwriterRole);
    } catch (error) {
      return Result.err(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  private async getSurefyreUser(
    userId: number
  ): Promise<Result<SurefyreUser, Error>> {
    try {
      const url = `${config.surefyre.getUserUrl}/${userId.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.config.apiKey,
        },
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorBody = await response.text();
          if (errorBody) {
            errorMessage += ` - ${errorBody}`;
          }
        } catch (e) {
          return Result.err(
            new Error("Could not read user error response body")
          );
        }
        return Result.err(new Error(errorMessage));
      }

      const data: SurefyreUser = await response.json();
      return Result.ok(data);
    } catch (error) {
      return Result.err(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  getProviderName(): string {
    return "Surefyre";
  }
}
